import {z} from 'zod'
import {useState, useEffect} from 'react'

const COOKIE_NAME = 'lara-landing-settings'

const landingSettingsSchema = z.object({
  isLanguageSuggestionDismissed: z.boolean().default(false)
})

type LandingSettings = z.infer<typeof landingSettingsSchema>

const getCookieValue = (): LandingSettings | null => {
  if (typeof document === 'undefined') return null

  const cookies = document.cookie.split(';')
  const cookie = cookies.find(c => c.trim().startsWith(`${COOKIE_NAME}=`))

  if (!cookie) return null

  try {
    const value = JSON.parse(decodeURIComponent(cookie.split('=')[1])) as unknown
    const parsed = landingSettingsSchema.safeParse(value)
    return parsed.success ? parsed.data : null
  } catch {
    return null
  }
}

const updateCookieValue = (value: LandingSettings) => {
  const expirationDate = new Date()
  expirationDate.setDate(expirationDate.getDate() + 7) // 7 days from now

  const cookieOptions = {
    expires: expirationDate.toUTCString(),
    sameSite: 'lax',
    secure: true,
    path: '/'
  }

  const cookieString = Object.entries(cookieOptions)
    .map(([key, value]) => `${key}=${value}`)
    .join('; ')

  document.cookie = `${COOKIE_NAME}=${encodeURIComponent(JSON.stringify(value))}; ${cookieString}`
}

export const useLandingSettings = () => {
  const [settings, setSettings] = useState<LandingSettings>({
    isLanguageSuggestionDismissed: false
  })

  useEffect(() => {
    const cookieSettings = getCookieValue()
    if (cookieSettings) {
      setSettings(cookieSettings)
    }
  }, [])

  const updateSettings = (newSettings: Partial<LandingSettings>) => {
    const updatedSettings = landingSettingsSchema.parse({
      ...settings,
      ...newSettings
    })
    setSettings(updatedSettings)
    updateCookieValue(updatedSettings)
  }

  return {
    settings,
    updateSettings
  }
}
